/**
 * KeyboardService
 * 
 * Central orchestrator for all keyboard input. Handles:
 * - Event normalization across browsers and OSs.
 * - Active key state tracking (Set of currently down keys).
 * - History tracking (Set of all keys pressed since last reset).
 * - System-level key locking (Meta, Tab, etc.) via the Keyboard Lock API.
 * - Smart prevention of browser default behaviors to ensure a "clean room" testing environment.
 */
export class KeyboardService extends EventTarget {
  constructor() {
    super();
    this.activeKeys = new Set();
    this.historyKeys = new Set();
    this.maxSimultaneous = 0;
    this.isSystemLocked = false;
    this.isPaused = false;

    this.init();
  }

  init() {
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));
    window.addEventListener('keyup', (e) => this.handleKeyUp(e));

    // 🔥 FIXED: Don't clear keys on blur by default.
    // Most professional testers keep the state even if you alt-tab or click away.
    // We only clear if the user explicitly clicks the Reset button.
    window.addEventListener('blur', () => {
      console.log('Focus lost (blur) - preserving key states for stability.');
      // Optionally dispatch a "focus-lost" event if any service needs it, 
      // but don't call clearAll().
    });
  }

  /**
   * Requests a system-wide lock of specific keys.
   * This prevents keys like Meta (Win/Cmd), Tab, and Alt+Tab from triggering OS or browser 
   * default behaviors, allowing the tester to capture them directly.
   * 
   * @async
   * @returns {Promise<boolean>} True if the lock was successfully granted.
   */
  async lockSystemKeys() {
    if (!navigator.keyboard || !navigator.keyboard.lock) {
      console.warn('Keyboard Lock API not supported in this browser.');
      return false;
    }

    try {
      // Lock system keys like Meta (Windows), Tab, Alt+Tab, etc.
      await navigator.keyboard.lock(['MetaLeft', 'MetaRight', 'Tab', 'AltLeft', 'AltRight', 'Escape']);
      this.isSystemLocked = true;
      this.dispatchEvent(new CustomEvent('lockchange', { detail: { locked: true } }));
      return true;
    } catch (err) {
      console.error('Failed to lock keyboard:', err);
      return false;
    }
  }

  /**
   * Releases all currently held keyboard locks.
   */
  unlockSystemKeys() {
    if (navigator.keyboard && navigator.keyboard.unlock) {
      navigator.keyboard.unlock();
      this.isSystemLocked = false;
      this.dispatchEvent(new CustomEvent('lockchange', { detail: { locked: false } }));
    }
  }

  /**
   * Handles the 'keydown' event. 
   * Responsibilities:
   * 1. Filters events arriving while window is out of focus.
   * 2. Normalizes tricky keys (like PrintScreen) that lack consistent 'keyup' signals.
   * 3. Prevents default browser actions for navigation/shortcut keys.
   * 4. Updates active and history sets.
   * 5. Dispatches normalized 'keychange' events.
   * 
   * @param {KeyboardEvent} e - The raw browser keyboard event.
   */
  handleKeyDown(e) {
    // 🔥 ENHANCED: Only detect if window is truly focused and tester is not paused
    if (!document.hasFocus() || this.isPaused) return;

    const code = e.code;
    const key = e.key;

    // Special handling for PrintScreen - it often doesn't fire keyup correctly 
    // especially if the OS takes focus for a screenshot tool.
    if (code === 'PrintScreen' || key === 'PrintScreen') {
      // Force immediate visual feedback then auto-release
      this.activeKeys.add(code);
      this.historyKeys.add(code);
      this.dispatchEvent(new CustomEvent('keychange', {
        detail: { type: 'down', code, key, activeCount: this.activeKeys.size, maxCount: this.maxSimultaneous, originalEvent: e }
      }));
      setTimeout(() => {
        this.activeKeys.delete(code);
        this.dispatchEvent(new CustomEvent('keychange', {
          detail: { type: 'up', code, key, activeCount: this.activeKeys.size, maxCount: this.maxSimultaneous, originalEvent: e }
        }));
      }, 200);
      return;
    }

    // Prevent browser default actions (F1-F12, Arrows, PgUp/Down, etc.)
    if (this.shouldPreventDefault(e)) {
      e.preventDefault();
    }

    // Normalize and store
    this.activeKeys.add(code);
    this.historyKeys.add(code);

    // Track max simultaneous
    if (this.activeKeys.size > this.maxSimultaneous) {
      this.maxSimultaneous = this.activeKeys.size;
    }

    // Special handling for Meta/Win key
    if (code === 'MetaLeft' || code === 'MetaRight' || key === 'Meta') {
      // If NOT in system lock mode, the Win menu will open and we'll lose focus.
      // We keep it highlighted for at least 300ms so the user sees the hit.
      setTimeout(() => {
        if (!this.isSystemLocked && this.activeKeys.has(code)) {
          this.handleKeyUp({ code, key, preventDefault: () => { } });
        }
      }, 300);
    }

    this.dispatchEvent(new CustomEvent('keychange', {
      detail: {
        type: 'down',
        code,
        key,
        repeat: e.repeat,
        activeCount: this.activeKeys.size,
        maxCount: this.maxSimultaneous,
        originalEvent: e
      }
    }));
  }

  handleKeyUp(e) {
    // 🔥 NEW: Prevent background detection or new events while paused
    if ((!document.hasFocus() || this.isPaused) && !this.activeKeys.has(e.code)) {
      return;
    }

    const code = e.code;
    const key = e.key;

    // Prevent browser default actions
    if (this.shouldPreventDefault(e)) {
      if (e.preventDefault) e.preventDefault();
    }

    // Special handling for Win key release - it often fires after focus loss
    if (code === 'MetaLeft' || code === 'MetaRight' || key === 'Meta') {
      this.activeKeys.delete(code);
    }

    this.activeKeys.delete(code);

    this.dispatchEvent(new CustomEvent('keychange', {
      detail: {
        type: 'up',
        code,
        key,
        activeCount: this.activeKeys.size,
        maxCount: this.maxSimultaneous,
        originalEvent: e
      }
    }));
  }

  /**
   * Determines if a specific key event should be suppressed (preventDefault).
   * We suppress keys that would otherwise navigate away from the page, 
   * scroll the document, or trigger browser-level menus.
   * 
   * @param {KeyboardEvent} e - The raw keyboard event or a mock event object.
   * @returns {boolean} True if the event should be prevented.
   */
  shouldPreventDefault(e) {
    const { code, key, ctrlKey, altKey, metaKey } = e;

    // If system is locked, we want to prevent almost everything to capture it
    if (this.isSystemLocked) {
      // Still allow some critical browser shortcuts if needed, but for testing we prevent most
      if (key === 'F5' || (ctrlKey && key === 'r')) return false; // Allow refresh
      if (key === 'F12' || (ctrlKey && key === 'i')) return false; // Allow DevTools
      return true;
    }

    // Default behavior mapping
    const preventKeys = [
      'Tab', 'Alt', 'Meta', 'ContextMenu', // Modifiers/Navigation
      'F1', 'F2', 'F3', 'F4', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', // F-keys (except DevTools/Refresh)
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', // Arrows (prevent scroll)
      'PageUp', 'PageDown', 'Home', 'End', ' ', 'Space',// Navigation (prevent scroll)
      'Backspace', 'Delete', // Prevent back navigation in some browsers
      'Enter', 'NumpadEnter' // Prevent accidental clicks on focused buttons
    ];

    if (preventKeys.includes(key) || preventKeys.includes(code)) return true;

    // Prevent some CTRL/ALT combos (like Ctrl+S, Ctrl+P)
    if ((ctrlKey || altKey) && !['KeyR', 'KeyI'].includes(code)) return true;

    return false;
  }

  clearAll() {
    this.activeKeys.clear();
    this.historyKeys.clear();
    this.maxSimultaneous = 0;
    this.dispatchEvent(new CustomEvent('clear'));
  }

  getActiveKeys() {
    return Array.from(this.activeKeys);
  }

  getHistoryKeys() {
    return Array.from(this.historyKeys);
  }

  getMaxSimultaneous() {
    return this.maxSimultaneous;
  }
}

export const keyboardService = new KeyboardService();
